import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import axios from '../../utils/axiosConfig';
import { toast } from '../../utils/toast';

import logger from '../../utils/logger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`club-selection-tabpanel-${index}`}
      aria-labelledby={`club-selection-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Club {
  _id: string;
  name: string;
  address: {
    street: string;
    postalCode: string;
    city: string;
  };
}

interface UserAddDialogProps {
  open: boolean;
  onClose: () => void;
  onUserAdded: () => void;
}

interface UserData {
  name: string;
  email: string;
  password: string;
  role: string;
  isApproved: boolean;
  club?: string;
  createNewClub?: boolean;
  clubName?: string;
  clubAddress?: {
    street: string;
    postalCode: string;
    city: string;
  };
}

const UserAddDialog: React.FC<UserAddDialogProps> = ({
  open,
  onClose,
  onUserAdded
}) => {
  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('user');
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  
  // New club form
  const [clubName, setClubName] = useState('');
  const [clubStreet, setClubStreet] = useState('');
  const [clubPostalCode, setClubPostalCode] = useState('');
  const [clubCity, setClubCity] = useState('');
  
  // UI states
  const [tabValue, setTabValue] = useState(0);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load clubs when dialog opens
  useEffect(() => {
    if (open) {
      fetchClubs();
      // Reset form on open
      resetForm();
    }
  }, [open]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/clubs');
      setClubs(response.data);
      setError(null);
    } catch (err: any) {
      setError('Fehler beim Laden der Vereine');
      //console.error('Error fetching clubs:', err);
      logger.error('Error fetching clubs', { error: err });
      toast.error(err.response?.data?.message || 'Fehler beim Laden der Vereine');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setRole('user');
    setSelectedClubId('');
    setClubName('');
    setClubStreet('');
    setClubPostalCode('');
    setClubCity('');
    setTabValue(0);
    setFormErrors({});
    setError(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value);
  };

  const handleClubChange = (event: SelectChangeEvent) => {
    setSelectedClubId(event.target.value);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!name.trim()) errors.name = 'Name ist erforderlich';
    if (!email.trim()) errors.email = 'E-Mail ist erforderlich';
    if (!email.includes('@')) errors.email = 'Ungültige E-Mail-Adresse';
    if (!password || password.length < 6) errors.password = 'Passwort muss mindestens 6 Zeichen haben';
    
    // Verein ist nur für 'user' oder 'organizer' erforderlich
    if (role === 'user' || role === 'organizer') {
      if (tabValue === 0 && !selectedClubId) {
        errors.club = 'Bitte wähle einen Verein aus';
      } else if (tabValue === 1) {
        if (!clubName.trim()) errors.clubName = 'Vereinsname ist erforderlich';
        if (!clubStreet.trim()) errors.clubStreet = 'Straße ist erforderlich';
        if (!clubPostalCode.trim()) errors.clubPostalCode = 'PLZ ist erforderlich';
        if (!clubCity.trim()) errors.clubCity = 'Stadt ist erforderlich';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      let userData: UserData = {
        name,
        email,
        password,
        role,
        isApproved: true // Von Admin erstellte Benutzer sind automatisch freigeschaltet
      };
      
      // Verein zuweisen oder erstellen
      if (tabValue === 0 && selectedClubId) {
        // Existierenden Verein zuweisen
        userData = {
          ...userData,
          club: selectedClubId
        };
      } else if (tabValue === 1 && clubName) {
        // Neuen Verein erstellen
        userData = {
          ...userData,
          createNewClub: true,
          clubName: clubName,
          clubAddress: {
            street: clubStreet,
            postalCode: clubPostalCode,
            city: clubCity
          }
        };
      }
      
      //console.log('Sende Benutzerdaten:', userData);
      
      // Benutzer erstellen
      const response = await axios.post('/api/admin/users', userData);
      
      //console.log('Server-Antwort:', response.data);
      
      toast.success('Benutzer erfolgreich angelegt');
      onUserAdded();
      onClose();
    } catch (err: any) {
      //console.error('Fehler beim Erstellen des Benutzers:', err);
      logger.error('Error creating user', { error: err });
      const errorMessage = err.response?.data?.message || 'Fehler beim Erstellen des Benutzers';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Neuen Benutzer anlegen
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
          
          <TextField
            fullWidth
            label="E-Mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />
          
          <TextField
            fullWidth
            label="Passwort"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />
          
          <FormControl fullWidth>
            <InputLabel id="role-select-label">Rolle</InputLabel>
            <Select
              labelId="role-select-label"
              value={role}
              label="Rolle"
              onChange={handleRoleChange}
            >
              <MenuItem value="user">Benutzer</MenuItem>
              <MenuItem value="organizer">Organisator</MenuItem>
              <MenuItem value="helper">Orga-Helfer</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
            </Select>
          </FormControl>
          
          {/* Vereinsauswahl für alle Rollen möglich */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Vereinszuordnung {role === 'user' || role === 'organizer' ? '(erforderlich)' : '(optional)'}
            </Typography>
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Existierenden Verein auswählen" id="club-selection-tab-0" />
                <Tab label="Neuen Verein anlegen" id="club-selection-tab-1" />
              </Tabs>
            </Box>
            
            <TabPanel value={tabValue} index={0}>
              <FormControl fullWidth error={!!formErrors.club}>
                <InputLabel id="club-select-label">Verein</InputLabel>
                <Select
                  labelId="club-select-label"
                  value={selectedClubId}
                  label="Verein"
                  onChange={handleClubChange}
                >
                  {clubs.map((club) => (
                    <MenuItem key={club._id} value={club._id}>
                      {club.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.club && <FormHelperText>{formErrors.club}</FormHelperText>}
              </FormControl>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Vereinsname"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  error={!!formErrors.clubName}
                  helperText={formErrors.clubName}
                />
                
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Adresse
                </Typography>
                
                <TextField
                  fullWidth
                  label="Straße"
                  value={clubStreet}
                  onChange={(e) => setClubStreet(e.target.value)}
                  error={!!formErrors.clubStreet}
                  helperText={formErrors.clubStreet}
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="PLZ"
                    value={clubPostalCode}
                    onChange={(e) => setClubPostalCode(e.target.value)}
                    error={!!formErrors.clubPostalCode}
                    helperText={formErrors.clubPostalCode}
                  />
                  
                  <TextField
                    fullWidth
                    label="Stadt"
                    value={clubCity}
                    onChange={(e) => setClubCity(e.target.value)}
                    error={!!formErrors.clubCity}
                    helperText={formErrors.clubCity}
                  />
                </Box>
              </Box>
            </TabPanel>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Wird erstellt...' : 'Benutzer erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserAddDialog;